import { Color, Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, MoreIcon } from '@persona-id/design-tokens/icons';
import { StarFillIcon } from '@persona-id/design-tokens/icons/12px';
import { DateTime } from 'luxon';
import { useMemo, VFC } from 'react';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { createInputStep, generateStepContext } from 'dashboard/lib/workflows';
import { getTriggerDescription } from 'dashboard/lib/workflows/trigger';
import { TransactionType } from 'dashboard/models/transaction-type';
import { Trigger, TriggerEventAttributes } from 'dashboard/models/trigger';
import { Workflow, WorkflowSchema, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowDeployment } from 'dashboard/models/workflow-deployment';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { usePlatformObjectJsonSchema } from 'dashboard/redux/selectors/workflow-editor-context';
import WorkflowCardTriggerCriteria from 'dashboard/views/DashboardWorkflowsView/WorkflowCardTriggerCriteria';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Sticker, { LabelBackgrounds } from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

const StyledCard = styled(Card)`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${Spacing[5]} ${Spacing[6]};
`;

const TitleContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
  margin-left: ${Spacing[3]};
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
`;

const WorkflowTitle = styled(Link)`
  font-weight: 500;
  font-size: 20px;
  line-height: 150%;
  display: flex;
  align-items: center;
  letter-spacing: 0.15px;
  color: ${({ theme }) => theme.color.text.primary};
  overflow: hidden;

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledSticker = styled(Sticker)`
  height: 20px;
  margin-left: ${Spacing[4]};
  min-width: 88px;
`;

const TimestampText = styled(Paragraph)`
  font-weight: 400;
  font-size: 12px;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const StyledTooltip = styled(Tooltip).attrs(() => ({ placement: 'left', trigger: 'hover' }))`
  margin-top: ${Spacing[1]};
  margin-left: ${Spacing[2]};

  & > div {
    max-width: 360px;
  }
`;

const CardBody = styled('div')`
  grid-template-columns: 1fr 200px;
  display: grid;
  margin-top: ${Spacing[2]};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr 8fr;
  grid-auto-flow: row;
  > * {
    font-size: 14px;
  }
`;

const LabelText = styled(Paragraph)`
  font-style: normal;
  font-weight: 400;
  color: ${({ theme }) => theme.color.text.secondary};
`;

const ButtonsContainer = styled.div`
  margin-top: ${Spacing[4]};
  display: flex;
  justify-content: flex-end;

  & > button:first-child {
    width: 72px;
  }
  & > button:nth-child(2) {
    width: 32px;
    margin-left: ${Spacing[2]};
  }
`;

interface WorkflowCardProps {
  isLoadingSchema: boolean;
  onArchiveWorkflow: (workflow: Workflow) => void;
  onCloneWorkflow: (workflow: Workflow) => void;
  onPauseWorkflow: (workflow: Workflow) => void;
  onRunWorkflow: (workflow: Workflow) => void;
  workflowDeployment?: WorkflowDeployment;
  workflow: Workflow;
  workflowVersion: WorkflowVersion;
  trigger: Trigger;
  workflowSchema: WorkflowSchema;
  transactionTypes: TransactionType[];
}

const WorkflowCard: VFC<WorkflowCardProps> = ({
  isLoadingSchema,
  onArchiveWorkflow,
  onCloneWorkflow,
  onPauseWorkflow,
  onRunWorkflow,
  workflow,
  workflowVersion,
  trigger,
  workflowSchema,
  transactionTypes,
}) => {
  const {
    id: workflowId,
    attributes: { status: workflowStatus, name: workflowName },
  } = workflow;

  const navigate = useNavigate();

  const handleClickEdit = () => {
    navigate(`/dashboard/workflows/${workflowId}/${workflowVersion.id}`);
  };

  const hasWorkflowRunListPermission = usePermission(Permission.WorkflowRunList);
  const canWrite = usePermission(Permission.WorkflowWrite);

  const isDashboardWorkflowCloneEnabled = useFeatureFlag('dashboard:enable-workflows-clone');

  const moreMenuItems = [
    hasWorkflowRunListPermission && (
      <DropdownMenu.Item key='runs' as={Link} to={`/dashboard/workflows/${workflowId}/runs`}>
        Runs
      </DropdownMenu.Item>
    ),
    canWrite && workflowStatus !== WorkflowStatus.Paused && (
      <DropdownMenu.Item
        id='workflow-card__dropdown-deactivate'
        key='deactivate'
        onClick={() => onPauseWorkflow(workflow)}
      >
        Deactivate
      </DropdownMenu.Item>
    ),
    canWrite &&
      workflow.relationships['latest-published-version'].data?.id &&
      workflowStatus !== WorkflowStatus.Running && (
        <DropdownMenu.Item
          id='workflow-card__dropdown-activate'
          key='activate'
          onClick={() => onRunWorkflow(workflow)}
        >
          Activate
        </DropdownMenu.Item>
      ),
    isDashboardWorkflowCloneEnabled && canWrite && (
      <DropdownMenu.Item
        id='workflow-card__dropdown-make-a-copy'
        key='clone'
        onClick={() => onCloneWorkflow(workflow)}
      >
        Make a copy
      </DropdownMenu.Item>
    ),
    canWrite && (
      <DropdownMenu.Item
        id='workflow-card__dropdown-archive'
        key='archive'
        variant='danger'
        onClick={() => onArchiveWorkflow(workflow)}
      >
        Archive
      </DropdownMenu.Item>
    ),
  ].filter(Boolean);

  const statusDescriptor = useMemo(() => {
    return `Last edited at ${DateTime.fromISO(workflowVersion.attributes['updated-at']).toFormat(
      DATE_AND_TIME_TZ_FORMAT,
    )}`;
  }, [workflowVersion.attributes]);

  const platformObjectJsonSchema = usePlatformObjectJsonSchema();
  const usableObjects = useMemo(() => {
    return generateStepContext({
      step: createInputStep({ trigger }),
      platformObjectJsonSchema,
      genericWorkflowActions: workflowSchema['generic-workflow-actions'],
      criteriaVariableSchemas: workflowSchema['criteria-variable-schemas'],
    });
  }, [platformObjectJsonSchema, trigger, workflowSchema]);

  return (
    <StyledCard>
      <TitleContainer>
        <LeftContainer>
          <WorkflowTitle to={`/dashboard/workflows/${workflowId}/${workflowVersion.id}`}>
            <span data-test={`workflow-card__name__${workflowId}`}>{workflowName}</span>
          </WorkflowTitle>
          {workflowVersion.attributes['enhanced'] && (
            <StyledSticker
              variant='primary'
              color={Color.PrimaryBlack}
              data-test={`workflow-card__enhanced-sticker-${workflowId}`}
              icon={<StarFillIcon />}
            >
              Enhanced
            </StyledSticker>
          )}
          {workflow.attributes.description && (
            <StyledTooltip iconTrigger={<CircleInfoIcon />}>
              <div>{workflow.attributes.description}</div>
            </StyledTooltip>
          )}
        </LeftContainer>
        <RightContainer>
          <TimestampText>{statusDescriptor}</TimestampText>
        </RightContainer>
      </TitleContainer>
      <CardBody>
        <InfoGrid>
          <LabelText>Status</LabelText>
          <LabelText>Trigger</LabelText>
          <LabelText>Trigger criteria</LabelText>
          <div>
            <StatusSticker status={workflow.attributes.active} />
          </div>
          <Paragraph color='primary' data-test='workflow-card__trigger-name'>
            {getTriggerDescription(
              trigger.type,
              (trigger.attributes as TriggerEventAttributes)?.['event-name'],
            )}
          </Paragraph>
          <WorkflowCardTriggerCriteria
            trigger={trigger}
            usableObjects={usableObjects}
            isLoadingSchema={isLoadingSchema}
            transactionTypes={transactionTypes}
          />
        </InfoGrid>
        <ButtonsContainer>
          <Button
            variant='basic'
            width='auto'
            onClick={handleClickEdit}
            id='workflow-card__edit_button'
          >
            {canWrite ? 'Edit' : 'View'}
          </Button>
          {moreMenuItems.length > 0 && (
            <DropdownMenu
              trigger={
                <Button
                  aria-label={`More actions for ${workflowName}`}
                  id={`workflow_card__more-actions-${workflowId}`}
                  variant='basic'
                  width='icon'
                >
                  <MoreIcon />
                </Button>
              }
            >
              {moreMenuItems}
            </DropdownMenu>
          )}
        </ButtonsContainer>
      </CardBody>
    </StyledCard>
  );
};

interface StatusStickerProps {
  status: boolean;
}

const StatusSticker = ({ status }: StatusStickerProps) => {
  const getStickerStatus = (): [Partial<LabelBackgrounds>, string] => {
    switch (status) {
      case true: {
        return ['green', 'Active'];
      }
      case false: {
        return ['grey', 'Inactive'];
      }
      default: {
        return ['darkGrey', 'n/a'];
      }
    }
  };

  const [variantType, statusMessage] = getStickerStatus();

  return (
    <Sticker variant={variantType} data-test='workflow-status-sticker__sticker'>
      {statusMessage}
    </Sticker>
  );
};

export default WorkflowCard;
